import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, UploadCloud, Image as ImageIcon, RotateCcw, Eye } from 'lucide-react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FooterActions,
  // FormRow,
  FormGroup,
  InputLabel,
  TextInput,
  TextArea,
  ErrorText,
  LogoUploadRow,
  LogoUploadBox,
  LogoPreviewRow,
  LogoThumb,
  LogoFileName,
  LogoActions,
  LogoIconButton,
  UploadLogoButton,
  RemoveLogoButton,
  HiddenFileInput,
  ModalCancelButton,
  ModalSubmitButton,
} from './CreateAndUpdateClientModal.styles';
import { useCreateClient, useUpdateClient, useViewDocument } from '../hooks/useClientsAdmin';
import { useUploadClientLogo } from '../hooks/useClientLogoUpload';

const EMPTY_LOGO = null; // { documentId, name, fileSize, contentType, url?, previewUrl? }

const generateFolderName = () => {
  const random = Math.floor(1000 + Math.random() * 9000);
  return `${Date.now()}${random}`;
};

const toLogoPayload = (logo) => {
  if (!logo) return logo;
  const { documentId, name, fileSize, contentType } = logo;
  return { documentId, name, fileSize, contentType };
};

const CreateAndUpdateClientModal = ({ isOpen, onClose, editingClient }) => {
  const isEditMode = Boolean(editingClient);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [folderName, setFolderName] = useState(generateFolderName);
  // const [clientCode, setClientCode] = useState('');
  const [lightLogo, setLightLogo] = useState(EMPTY_LOGO);
  const [darkLogo, setDarkLogo] = useState(EMPTY_LOGO);
  const [uploadingSlot, setUploadingSlot] = useState(null); // 'light' | 'dark' | null
  const [viewingSlot, setViewingSlot] = useState(null); // 'light' | 'dark' | null
  const [errors, setErrors] = useState({});

  const lightInputRef = useRef(null);
  const darkInputRef = useRef(null);

  const uploadFolderName = isEditMode ? editingClient?.folderName : folderName;

  const uploadLogoMutation = useUploadClientLogo();

  const viewDocumentMutation = useViewDocument({
    onError: (err) => {
      window.alert(
        `Failed to open logo${err?.message ? `: ${err.message}` : '. Please try again.'}`
      );
    },
  });

  const createClientMutation = useCreateClient({
    onSuccess: () => {
      resetState();
      onClose?.();
    },
    onError: (err) => {
      window.alert(`Failed to create client${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  const updateClientMutation = useUpdateClient({
    onSuccess: () => {
      resetState();
      onClose?.();
    },
    onError: (err) => {
      window.alert(`Failed to update client${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  const isSubmitting = createClientMutation.isPending || updateClientMutation.isPending;

  useEffect(() => {
    if (!isOpen) return;

    if (editingClient) {
      setName(editingClient.name || '');
      setDescription(editingClient.description || '');
      setFolderName(editingClient.folderName || '');
      // setClientCode(editingClient.clientCode || '');
      setLightLogo(
        editingClient.lightModeLogo
          ? {
              documentId: editingClient.lightModeLogo.documentId,
              name: editingClient.lightModeLogo.name,
              fileSize: editingClient.lightModeLogo.fileSize,
              contentType: editingClient.lightModeLogo.contentType,
              url: editingClient.lightModeLogo.url,
            }
          : EMPTY_LOGO
      );
      setDarkLogo(
        editingClient.darkModeLogo
          ? {
              documentId: editingClient.darkModeLogo.documentId,
              name: editingClient.darkModeLogo.name,
              fileSize: editingClient.darkModeLogo.fileSize,
              contentType: editingClient.darkModeLogo.contentType,
              url: editingClient.darkModeLogo.url,
            }
          : EMPTY_LOGO
      );
    } else {
      resetState(false);
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingClient]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isSubmitting) handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSubmitting]);

  if (!isOpen) return null;

  const resetState = (clearOpenFlag = true) => {
    setName('');
    setDescription('');
    setFolderName(generateFolderName());
    // setClientCode('');
    setLightLogo(EMPTY_LOGO);
    setDarkLogo(EMPTY_LOGO);
    setErrors({});
    if (clearOpenFlag) {
      setUploadingSlot(null);
      setViewingSlot(null);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose?.();
  };

  const handleLogoUploadClick = (slot) => {
    (slot === 'light' ? lightInputRef : darkInputRef).current?.click();
  };

  const handleLogoFileSelected = (slot) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!uploadFolderName) {
      window.alert('Could not find the storage folder for this client.');
      return;
    }

    setUploadingSlot(slot);
    uploadLogoMutation.mutate(
      { file, folderName: uploadFolderName },
      {
        onSuccess: (result) => {
          const nextLogo = {
            documentId: result.documentId,
            name: result.name,
            fileSize: result.fileSize,
            contentType: result.contentType,
            previewUrl: URL.createObjectURL(file),
          };
          if (slot === 'light') setLightLogo(nextLogo);
          else setDarkLogo(nextLogo);
          setUploadingSlot(null);
        },
        onError: (err) => {
          setUploadingSlot(null);
          window.alert(
            `Failed to upload ${slot === 'light' ? 'light-mode' : 'dark-mode'} logo${
              err?.message ? `: ${err.message}` : '. Please try again.'
            }`
          );
        },
      }
    );
  };

  // const handleViewLogo = (slot, logo) => {
  //   if (!logo?.documentId || !editingClient?._id || viewingSlot) return;

  //   setViewingSlot(slot);
  //   viewDocumentMutation.mutate(
  //     { documentId: logo.documentId, clientId: editingClient._id, folderName: uploadFolderName },
  //     { onSettled: () => setViewingSlot(null) }
  //   );
  // };

  const handleViewLogo = (slot, logo) => {
    const url = logo?.url || logo?.previewUrl;
    if (!url) {
      window.alert('Logo URL is not available.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // "Remove" never calls a delete API — it just clears local state so the
  // user can re-upload a replacement, which is all re-uploading needs.
  const handleRemoveLogo = (slot) => {
    if (slot === 'light') setLightLogo(EMPTY_LOGO);
    else setDarkLogo(EMPTY_LOGO);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEditMode) {
      const nextErrors = {};
      if (!name.trim()) nextErrors.name = 'Name is required';
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      updateClientMutation.mutate({
        clientId: editingClient._id,
        name: name.trim(),
        description: description.trim(),
        ...(lightLogo && { lightModeLogo: toLogoPayload(lightLogo) }),
        ...(darkLogo && { darkModeLogo: toLogoPayload(darkLogo) }),
      });
      return;
    }

    const nextErrors = {};
    if (!name.trim()) nextErrors.name = 'Name is required';
    // if (!clientCode.trim()) nextErrors.clientCode = 'Client code is required';
    if (!lightLogo) nextErrors.lightLogo = 'Upload a light-mode logo';
    if (!darkLogo) nextErrors.darkLogo = 'Upload a dark-mode logo';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    createClientMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      folderName,
      // clientCode: clientCode.trim(),
      lightModeLogo: toLogoPayload(lightLogo),
      darkModeLogo: toLogoPayload(darkLogo),
    });
  };

  const renderLogoBox = (slot, logo) => {
    const isUploadingThis = uploadingSlot === slot;
    const isViewingThis = viewingSlot === slot;

    return (
      <FormGroup>
        <InputLabel as="span">{slot === 'light' ? 'Light-mode logo' : 'Dark-mode logo'}</InputLabel>
        <LogoUploadBox>
          {logo ? (
            <LogoPreviewRow>
              <LogoThumb slot={slot}>
                {logo.previewUrl || logo.url ? (
                  <img src={logo.previewUrl || logo.url} alt="" />
                ) : (
                  <ImageIcon />
                )}
              </LogoThumb>
              <LogoFileName title={logo.name}>{logo.name}</LogoFileName>

              {isEditMode ? (
                <LogoActions>
                  <LogoIconButton
                    type="button"
                    title="View logo"
                    aria-label="View logo"
                    onClick={() => handleViewLogo(slot, logo)}
                    disabled={uploadingSlot !== null}
                  >
                    <Eye />
                  </LogoIconButton>
                  <LogoIconButton
                    type="button"
                    title="Re-upload logo"
                    aria-label="Re-upload logo"
                    onClick={() => handleLogoUploadClick(slot)}
                    disabled={uploadingSlot !== null}
                  >
                    {isUploadingThis ? (
                      <Loader2 style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <RotateCcw />
                    )}
                  </LogoIconButton>
                </LogoActions>
              ) : (
                <RemoveLogoButton
                  type="button"
                  title="Remove and re-upload"
                  onClick={() => handleRemoveLogo(slot)}
                >
                  <X />
                </RemoveLogoButton>
              )}
            </LogoPreviewRow>
          ) : (
            <UploadLogoButton
              type="button"
              onClick={() => handleLogoUploadClick(slot)}
              disabled={uploadingSlot !== null}
            >
              {isUploadingThis ? (
                <Loader2 style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <UploadCloud />
              )}
              {isUploadingThis ? 'Uploading…' : 'Upload logo'}
            </UploadLogoButton>
          )}
          <HiddenFileInput
            ref={slot === 'light' ? lightInputRef : darkInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoFileSelected(slot)}
          />
        </LogoUploadBox>
        {errors[slot === 'light' ? 'lightLogo' : 'darkLogo'] && (
          <ErrorText role="alert">{errors[slot === 'light' ? 'lightLogo' : 'darkLogo']}</ErrorText>
        )}
      </FormGroup>
    );
  };

  return createPortal(
    <ModalOverlay role="presentation">
      <ModalContent
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="client-modal-title">
            {isEditMode ? 'Edit client' : 'Add client'}
          </ModalTitle>
          <ModalCloseButton type="button" onClick={handleClose} aria-label="Close dialog">
            <X size={18} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {/* <FormRow> */}
            <FormGroup>
              <InputLabel htmlFor="clientName">Name</InputLabel>
              <TextInput
                id="clientName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc."
                $hasError={!!errors.name}
              />
              {errors.name && <ErrorText role="alert">{errors.name}</ErrorText>}
            </FormGroup>

            {/* <FormGroup>
                <InputLabel htmlFor="clientCode">Client code</InputLabel>
                <TextInput
                  id="clientCode"
                  type="text"
                  value={clientCode}
                  onChange={(e) => setClientCode(e.target.value)}
                  placeholder="DEVCLI12"
                  disabled={isEditMode}
                  $hasError={!!errors.clientCode}
                />
                {errors.clientCode && <ErrorText role="alert">{errors.clientCode}</ErrorText>}
                {isEditMode && (
                  <HelperNote>Use "Regenerate code" from the table to change this.</HelperNote>
                )}
              </FormGroup> */}
            {/* </FormRow> */}

            <FormGroup>
              <InputLabel htmlFor="clientDescription">Description</InputLabel>
              <TextArea
                id="clientDescription"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this client is for"
              />
            </FormGroup>

            <LogoUploadRow>
              {renderLogoBox('light', lightLogo)}
              {renderLogoBox('dark', darkLogo)}
            </LogoUploadRow>
          </ModalBody>

          <ModalFooter>
            <FooterActions>
              <ModalCancelButton type="button" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </ModalCancelButton>
              <ModalSubmitButton type="submit" disabled={isSubmitting || uploadingSlot !== null}>
                {isSubmitting && <Loader2 style={{ animation: 'spin 1s linear infinite' }} />}
                {isSubmitting
                  ? isEditMode
                    ? 'Saving…'
                    : 'Creating…'
                  : isEditMode
                    ? 'Save changes'
                    : 'Create client'}
              </ModalSubmitButton>
            </FooterActions>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default CreateAndUpdateClientModal;
